import { cn } from "@/lib/utils/cn";
import { toast } from "@/lib/toast";
import { Button } from "@/ui-components/ui/button";
import {
  Crop,
  Download,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Undo2,
  Upload,
  Wand2,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import ReactCrop, { Crop as ReactCropType, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import heic2any from "heic2any";
import { Skeleton } from "@/ui-components/ui/skeleton";
import {
  ManagedImageBusyAction,
  ManagedImageEditRequest,
  ManagedImageValue,
  attachManagedImageDraftMetadata,
  getManagedImageOriginalFile,
  getManagedImagePendingEdits,
} from "./managed-image.types";

interface ManagedImageFieldProps {
  value?: ManagedImageValue | null;
  file?: File | null;
  onFileChange: (file: File | null) => void;
  onDeleteRemote?: () => Promise<void> | void;
  onApplyEdit?: (edit: ManagedImageEditRequest) => Promise<void> | void;
  onRestoreOriginal?: () => Promise<void> | void;
  enableBackgroundRemoval?: boolean;
  enableCropping?: boolean;
  disabled?: boolean;
  busyAction?: ManagedImageBusyAction;
  className?: string;
  label?: string;
  emptyTitle?: string;
  maxSizeInMB?: number;
  previewBackgroundRemoval?: (file: File) => Promise<Blob>;
}

type ManagedImageProcessingMode = "crop" | "backgroundRemoval" | null;

const createCroppedFile = async (
  imageUrl: string,
  pixelCrop: PixelCrop,
  fileName: string,
  fileType: string
): Promise<File> => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.crossOrigin = "anonymous";
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
    nextImage.src = imageUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas Kontext konnte nicht erstellt werden");

  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (!value) reject(new Error("Bild konnte nicht zugeschnitten werden"));
      else resolve(value);
    }, fileType, 0.95);
  });

  return new File([blob], fileName, { type: fileType });
};

export const ManagedImageFieldSkeleton = ({ className }: { className?: string }) => {
  return (
    <div className={cn("flex flex-col space-y-4 relative", className)}>
      <Skeleton className="w-full min-h-[160px] rounded-xl" />
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 pt-2">
        <div className="flex gap-2 w-full sm:w-auto">
          <Skeleton className="h-9 flex-1 sm:w-28 rounded-lg" />
          <Skeleton className="h-9 flex-1 sm:w-28 rounded-lg" />
        </div>
      </div>
    </div>
  );
};

export const ManagedImageField = ({
  value,
  file,
  onFileChange,
  onDeleteRemote,
  onApplyEdit,
  onRestoreOriginal,
  enableBackgroundRemoval = true,
  enableCropping = true,
  disabled = false,
  busyAction = null,
  className,
  label = "Bild",
  emptyTitle = "Bild auswählen",
  maxSizeInMB = 10,
  previewBackgroundRemoval,
}: ManagedImageFieldProps) => {
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [isConvertingHeic, setIsConvertingHeic] = useState(false);
  const [isLocalProcessing, setIsLocalProcessing] = useState(false);
  const [processingMode, setProcessingMode] = useState<ManagedImageProcessingMode>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  
  // Cropping State
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState<ReactCropType>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  
  const hasPendingFile = !!file;

  useEffect(() => {
    let active = true;
    let currentUrl: string | null = null;

    const generatePreview = async () => {
      if (!file) {
        setLocalPreviewUrl(null);
        setIsGeneratingPreview(false);
        return;
      }

      setIsGeneratingPreview(true);

      if (file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic")) {
        setIsConvertingHeic(true);
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8,
          });
          const targetBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
          if (active) {
            currentUrl = URL.createObjectURL(targetBlob);
            setLocalPreviewUrl(currentUrl);
          }
        } catch (error) {
          console.error("Fehler beim Konvertieren des HEIC-Bildes für die Vorschau:", error);
          toast.error("Vorschau für HEIC-Bild konnte nicht geladen werden.");
        } finally {
          if (active) {
            setIsConvertingHeic(false);
            setIsGeneratingPreview(false);
          }
        }
      } else {
        currentUrl = URL.createObjectURL(file);
        if (active) {
          setLocalPreviewUrl(currentUrl);
          setIsGeneratingPreview(false);
        }
      }
    };

    generatePreview();

    return () => {
      active = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [file]);

  const activeVariantUrl = value?.active?.url;
  const activeDownloadUrl = value?.active?.downloadUrl;
  
  const previewUrl = hasPendingFile ? localPreviewUrl : activeVariantUrl ?? null;
  const previewDownloadUrl = hasPendingFile ? localPreviewUrl : activeDownloadUrl;
  const isImageLoading = previewUrl !== null && loadedUrl !== previewUrl;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        setIsGeneratingPreview(true);
        onFileChange(acceptedFiles[0]);
        setIsCropping(false);
      }
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
      "image/gif": [".gif"],
      "image/heic": [".heic"],
      "image/avif": [".avif"],
    },
    maxFiles: 1,
    maxSize: maxSizeInMB * 1024 * 1024,
    disabled: disabled || isCropping || isGeneratingPreview,
    noClick: true,
  });

  const triggerDownload = useCallback(() => {
    if (!previewDownloadUrl) return;
    const link = document.createElement("a");
    link.href = previewDownloadUrl;
    link.download = file?.name ?? "image.webp";
    link.target = "_blank";
    link.rel = "noreferrer";
    link.click();
  }, [previewDownloadUrl, file?.name]);

  const handleApplyCrop = async () => {
    if (!completedCrop || !previewUrl || !imgRef.current) return;
    
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    setProcessingMode("crop");

    if (hasPendingFile && file) {
      setIsLocalProcessing(true);
      try {
        const scaledCrop = {
          x: completedCrop.x * scaleX,
          y: completedCrop.y * scaleY,
          width: completedCrop.width * scaleX,
          height: completedCrop.height * scaleY,
          unit: 'px' as const,
        };

        let targetType = file.type || "image/png";
        let targetName = file.name;
        
        // Canvas can't encode HEIC/AVIF natively. We fallback to WebP to preserve quality/transparency
        // and prevent heic2any from trying to parse a PNG/WebP disguised as HEIC.
        if (targetType.includes("heic") || targetType.includes("avif") || targetName.toLowerCase().match(/\.(heic|avif)$/)) {
          targetType = "image/webp";
          targetName = targetName.replace(/\.[^/.]+$/, ".webp");
        }

        const croppedFile = await createCroppedFile(previewUrl, scaledCrop, targetName, targetType);
        
        const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
        const nx = clamp((completedCrop.x * scaleX) / imgRef.current.naturalWidth, 0, 1);
        const ny = clamp((completedCrop.y * scaleY) / imgRef.current.naturalHeight, 0, 1);
        const nWidth = clamp((completedCrop.width * scaleX) / imgRef.current.naturalWidth, 0, 1 - nx);
        const nHeight = clamp((completedCrop.height * scaleY) / imgRef.current.naturalHeight, 0, 1 - ny);

        const normalizedCrop = {
          x: nx,
          y: ny,
          width: nWidth,
          height: nHeight,
        };

        Object.assign(croppedFile, {
          originalFile: getManagedImageOriginalFile(file),
          pendingEdits: {
            ...getManagedImagePendingEdits(file),
            crop: normalizedCrop,
          },
        });
        
        onFileChange(croppedFile);
      } catch (err) {
        toast.error("Fehler beim Zuschneiden");
      } finally {
        setIsLocalProcessing(false);
        setProcessingMode(null);
      }
    } else if (onApplyEdit) {
      try {
        const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
        const nx = clamp((completedCrop.x * scaleX) / imgRef.current.naturalWidth, 0, 1);
        const ny = clamp((completedCrop.y * scaleY) / imgRef.current.naturalHeight, 0, 1);
        const nWidth = clamp((completedCrop.width * scaleX) / imgRef.current.naturalWidth, 0, 1 - nx);
        const nHeight = clamp((completedCrop.height * scaleY) / imgRef.current.naturalHeight, 0, 1 - ny);

        const normalizedCrop = {
          x: nx,
          y: ny,
          width: nWidth,
          height: nHeight,
        };
        
        await onApplyEdit({ crop: normalizedCrop });
      } finally {
        setProcessingMode(null);
      }
    } else {
      setProcessingMode(null);
    }
    setIsCropping(false);
  };


  const handleRemoveBackground = async () => {
    if (hasPendingFile && file) {
      if (!previewBackgroundRemoval) {
        toast.error("Hintergrundentfernung ist hier nicht verfügbar");
        return;
      }

      setProcessingMode("backgroundRemoval");
      setIsLocalProcessing(true);
      try {
        const processedBlob = await previewBackgroundRemoval(file);
        const newFile = new File([processedBlob], file.name.replace(/\.[^/.]+$/, ".webp"), { type: "image/webp" });
        
        attachManagedImageDraftMetadata(newFile, {
          originalFile: getManagedImageOriginalFile(file),
          pendingEdits: {
            ...getManagedImagePendingEdits(file),
            removeBackground: true,
          },
        });
        
        onFileChange(newFile);
        toast.success("Hintergrund entfernt");
      } catch (err) {
        toast.error("Fehler beim Entfernen des Hintergrunds");
      } finally {
        setIsLocalProcessing(false);
        setProcessingMode(null);
      }
    } else if (onApplyEdit) {
      setProcessingMode("backgroundRemoval");
      try {
        await onApplyEdit({ removeBackground: true });
      } finally {
        setProcessingMode(null);
      }
    }
  };

  const isDeleteBusy = busyAction === "delete";
  const isApplyEditBusy = busyAction === "applyEdit" || isLocalProcessing;
  const isSavingBusy = busyAction === "saving";
  const isRestoreBusy = busyAction === "restoreOriginal";
  const bgRemoved = hasPendingFile ? !!getManagedImagePendingEdits(file)?.removeBackground : value?.hasBackgroundRemoved;
  const showRainbowOverlay = processingMode === "backgroundRemoval";

  return (
      <>
      <style>{`
        @keyframes holographic {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div className={cn("flex flex-col space-y-4 relative", className)}>
      <div
        {...getRootProps()}
        onClick={(e) => {
          if (!disabled && !previewUrl && !isCropping && !isGeneratingPreview) open();
        }}
        className={cn(
          "group relative flex flex-col overflow-hidden rounded-xl border border-dashed transition-all duration-300",
          !disabled && !previewUrl && !isCropping && !isGeneratingPreview && "cursor-pointer",
          isDragActive ? "border-blue-400 bg-blue-50/50 ring-2 ring-blue-100" : "border-gray-200 bg-transparent hover:border-gray-300 hover:bg-gray-50/30",
          (disabled || isGeneratingPreview) && "cursor-not-allowed opacity-60",
          previewUrl ? "min-h-[240px] border-solid bg-transparent p-0" : "min-h-[160px]",
          isCropping && "ring-2 ring-blue-500/50 border-blue-500/50 bg-transparent"
        )}
      >
        <input {...getInputProps()} />
        {previewUrl ? (
          <div 
            className="relative flex flex-1 w-full flex-col items-center justify-center p-2 sm:p-4 rounded-xl"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0H8V8H0V0ZM8 8H16V16H8V8Z' fill='%23f1f5f9'/%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat",
            }}
          >
            {isCropping ? (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                className="max-h-[500px]"
              >
                <img
                  ref={imgRef}
                  src={previewUrl}
                  alt="Vorschau"
                  onLoad={() => previewUrl && setLoadedUrl(previewUrl)}
                  onError={() => previewUrl && setLoadedUrl(previewUrl)}
                  className="max-h-[300px] sm:max-h-[400px] w-auto max-w-full rounded-md shadow-sm"
                />
              </ReactCrop>
            ) : (
              <div className="relative flex w-full sm:w-auto sm:inline-block max-h-[300px] sm:max-h-[400px] flex-col items-center justify-center rounded-xl shadow-sm ring-1 ring-gray-200">
                <img
                  src={previewUrl}
                  alt="Vorschau"
                  onLoad={() => previewUrl && setLoadedUrl(previewUrl)}
                  onError={() => previewUrl && setLoadedUrl(previewUrl)}
                  className={cn(
                    "max-h-[300px] sm:max-h-[400px] w-auto max-w-full rounded-xl object-contain transition-opacity duration-300 ease-out",
                    isImageLoading ? "opacity-0" : "opacity-100"
                  )}
                />
                
                {/* Floating Actions on Image */}
                {!disabled && !isApplyEditBusy && (
                  <div className="absolute right-2 top-2 flex flex-col gap-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="secondary"
                      className="size-8 rounded-full shadow-sm opacity-90 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        hasPendingFile ? onFileChange(null) : onDeleteRemote?.();
                      }}
                    >
                      {isDeleteBusy ? <Loader2 className="size-4 animate-spin shrink-0" /> : <Trash2 className="size-4 text-red-600 shrink-0" />}
                    </Button>
                  </div>
                )}

                {!disabled && !isApplyEditBusy && (
                  <div className="absolute bottom-2 sm:bottom-4 left-1/2 flex w-max max-w-[calc(100%-16px)] -translate-x-1/2 flex-nowrap items-center justify-center gap-1.5 sm:gap-2 rounded-full bg-white/95 p-1.5 sm:px-2 sm:py-1.5 shadow-md ring-1 ring-gray-200 backdrop-blur-sm z-10">
                    {enableCropping && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 flex-none rounded-full px-2 sm:px-3 text-[11px] sm:text-xs whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCropping(true);
                        }}
                      >
                        <Crop className="mr-1.5 size-3.5 shrink-0" />
                        Zuschneiden
                      </Button>
                    )}
                    {enableBackgroundRemoval && (
                      <Button
                        type="button"
                        size="sm"
                        variant={bgRemoved ? "ghost" : "default"}
                        className={cn("h-8 flex-none rounded-full px-2 sm:px-3 text-[11px] sm:text-xs whitespace-nowrap", !bgRemoved && "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-600 hover:to-fuchsia-600")}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBackground();
                        }}
                        disabled={bgRemoved || (hasPendingFile && !previewBackgroundRemoval)}
                      >
                        <Wand2 className="mr-1.5 size-3.5 shrink-0" />
                        {bgRemoved ? "Entfernt" : "Freistellen"}
                      </Button>
                    )}
                  </div>
                )}

                <AnimatePresence>
                  {(isApplyEditBusy || isSavingBusy || isImageLoading || isConvertingHeic) && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 z-50 flex items-center justify-center rounded-xl overflow-hidden pointer-events-none"
                    >
                      {showRainbowOverlay ? (
                        <>
                          <div 
                            className="absolute inset-0"
                            style={{
                              background: 'linear-gradient(124deg, rgba(255,36,0,0.15), rgba(232,29,29,0.15), rgba(232,183,29,0.15), rgba(227,232,29,0.15), rgba(29,232,64,0.15), rgba(29,221,232,0.15), rgba(43,29,232,0.15), rgba(221,0,243,0.15))',
                              backgroundSize: '400% 400%',
                              animation: 'holographic 3s ease infinite',
                              backdropFilter: 'blur(8px) saturate(150%) brightness(1.1)',
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/5 mix-blend-overlay" />
                          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                        </>
                      ) : (
                        <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
                      )}
                      
                      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center">
                        {(!isApplyEditBusy || isSavingBusy) && (
                          <Loader2 className="h-8 w-8 animate-spin text-white drop-shadow-md mb-2" />
                        )}
                        {isSavingBusy && <span className="text-xs font-medium text-white drop-shadow-md">Speichern...</span>}
                        {isConvertingHeic && !isSavingBusy && <span className="text-xs font-medium text-white drop-shadow-md">Lade Vorschau...</span>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-full min-h-[160px] flex-col items-center justify-center px-6 py-8 text-center">
            {isGeneratingPreview ? (
              <div className="flex flex-col items-center justify-center animate-in fade-in duration-300">
                <div className="flex size-12 items-center justify-center rounded-xl bg-blue-50/50 shadow-sm border border-blue-100 mb-3">
                   <Loader2 className="size-5 animate-spin text-blue-600" />
                </div>
                <p className="text-sm font-semibold text-gray-900">Bild wird verarbeitet...</p>
                <p className="mt-1 max-w-xs text-xs text-gray-500">Bitte warten Sie einen Moment.</p>
              </div>
            ) : (
              <>
                <div className="flex size-12 items-center justify-center rounded-xl bg-white shadow-sm border border-gray-100 mb-3 transition-transform group-hover:scale-105">
                  {isDragActive ? <Upload className="size-5 text-blue-600" /> : <ImageIcon className="size-5 text-gray-400" />}
                </div>
                <p className="text-sm font-semibold text-gray-900">{isDragActive ? "Bild hier ablegen" : emptyTitle}</p>
                <p className="mt-1 max-w-xs text-xs text-gray-500">
                  Drag & Drop oder klick zum Auswählen.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {isCropping ? (
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:justify-end pt-2">
          <Button type="button" variant="ghost" size="sm" className="rounded-lg text-sm w-full sm:w-auto" onClick={() => setIsCropping(false)}>
            Abbrechen
          </Button>
          <Button type="button" size="sm" className="rounded-lg text-sm bg-gray-900 hover:bg-gray-800 w-full sm:w-auto" onClick={handleApplyCrop} disabled={!completedCrop?.width}>
            <Check className="mr-1.5 size-4 shrink-0" />
            Anwenden
          </Button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 pt-2">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="flex-1 sm:flex-none h-9 px-4 text-sm text-gray-700 bg-white border-gray-200 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all font-medium shadow-sm" onClick={open} disabled={disabled || isApplyEditBusy || isGeneratingPreview}>
              {isGeneratingPreview ? (
                <Loader2 className="mr-2 size-4 animate-spin shrink-0" />
              ) : (
                <Upload className="mr-2 size-4 shrink-0" />
              )}
              {isGeneratingPreview ? "Verarbeitet..." : (previewUrl ? "Ersetzen" : "Hochladen")}
            </Button>

            {previewDownloadUrl && (
              <Button type="button" variant="outline" size="sm" className="flex-1 sm:flex-none h-9 px-4 text-sm text-gray-700 bg-white border-gray-200 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all font-medium shadow-sm" onClick={triggerDownload} disabled={disabled || isApplyEditBusy}>
                <Download className="mr-2 size-4 shrink-0" />
                Download
              </Button>
            )}
          </div>

          {!hasPendingFile && value?.canRestoreOriginal && (
            <Button type="button" variant="outline" size="sm" className="h-9 px-4 text-sm text-gray-600 bg-gray-50 border-gray-200 border-dashed hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-all font-medium sm:ml-auto w-full sm:w-auto mt-1 sm:mt-0" onClick={() => onRestoreOriginal?.()} disabled={disabled || isRestoreBusy || isApplyEditBusy}>
              {isRestoreBusy ? <Loader2 className="mr-2 size-4 animate-spin shrink-0" /> : <Undo2 className="mr-2 size-4 shrink-0" />}
              Original wiederherstellen
            </Button>
          )}
        </div>
      )}
      </div>
      </>
    );
  };
